import { Button, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import TimezoneSelect, { ITimezone } from 'react-timezone-select';
import { IProps } from './types';
import { TIMEZONE, TIMEZONE_ABBREV, TIMEZONE_ALTNAME, updateUserTimezone } from '../../lib/messageUtils';

const Timezone = (props: IProps) => {
  const { initialValue, messagingUserId, openPopIn, onClose, onChange } = props
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:430px)');
  const now = new Date();
  const isDST = (new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Detroit',
    timeZoneName: 'short'
  }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value === 'EDT');

  const defaultTimezone: ITimezone = {
    value: "America/Detroit",
    label: isDST ? "(GMT-4:00) Eastern Time (EDT)" : "(GMT-5:00) Eastern Time (EST)",
    offset: isDST ? -4 : -5,
    abbrev: isDST ? "EDT" : "EST",
    altName: isDST ? "Eastern Daylight Time" : "Eastern Standard Time",
  };

  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(initialValue ? initialValue : defaultTimezone)

  const handleSave = async () => {
    if(selectedTimezone) {
      const timezone = (selectedTimezone as { value: string }).value
      const timezoneAbbrev = (selectedTimezone as { abbrev: string }).abbrev
      const timezoneAltName = (selectedTimezone as { altName: string }).altName
      await updateUserTimezone(messagingUserId, timezone)
      onChange(timezone)
      localStorage.setItem(TIMEZONE, timezone)
      localStorage.setItem(TIMEZONE_ALTNAME, timezoneAltName)
      localStorage.setItem(TIMEZONE_ABBREV, timezoneAbbrev)
    }
    
    onClose()
  }

  const handleCancel = () => {
    onClose()
  };

  const formatTimezoneDisplay = (timezone: ITimezone) => {
    const timezoneValue = (timezone as { value: string }).value
    const timezoneAltName = (timezone as { altName: string }).altName
    const timezoneAbbrev = (timezone as { abbrev: string }).abbrev

    const timezoneValueFromLocalStorage = localStorage.getItem(TIMEZONE) || ''
    const timezoneAltNameFromLocalStorage = localStorage.getItem(TIMEZONE_ALTNAME) || ''
    const timezoneAbbrevFromLocalStorage = localStorage.getItem(TIMEZONE_ABBREV) || ''

    let output = timezoneValue ?
                `Timezone: ${timezoneValue}\n Alt Name: ${timezoneAltName} (${timezoneAbbrev})` :
                `Timezone: ${timezoneValueFromLocalStorage}\n Alt Name: ${timezoneAltNameFromLocalStorage} (${timezoneAbbrevFromLocalStorage})`

    return output
  }

  return (
    <Dialog
      open={openPopIn}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          margin: isSmallScreen ? '16px' : '32px',
          width: isSmallScreen ? 'calc(100% - 32px)' : 'auto',
          maxWidth: isSmallScreen ? 'calc(100% - 32px)' : '600px',
          maxHeight: 'calc(100% - 64px)',
        }
      }}
    >
      <DialogTitle style={{
        padding: isSmallScreen ? '16px' : '24px',
        fontSize: isSmallScreen ? '1.1rem' : '1.25rem'
      }}>
         Timezone Settings 
      </DialogTitle>
      <DialogContent style={{
        minWidth: isSmallScreen ? 'auto' : '300px',
        padding: isSmallScreen ? '8px 16px' : '20px 24px',
        maxWidth: '100%'
      }}>
        <p style={{
          fontSize: isSmallScreen ? '0.9rem' : '1rem',
          margin: isSmallScreen ? '8px 0' : '16px 0'
        }}>
          Please select your preferred timezone:
        </p>
        <div className="select-wrapper" style={{
          width: '100%',
          marginBottom: isSmallScreen ? '12px' : '16px'
        }}>
          <TimezoneSelect
            labelStyle='abbrev'
            value={selectedTimezone}
            onChange={setSelectedTimezone}
            styles={{
              control: (provided: any) => ({
                ...provided,
                minHeight: isSmallScreen ? '44px' : '38px',
                fontSize: isSmallScreen ? '0.9rem' : '0.875rem'
              }),
              menu: (provided: any) => ({
                ...provided,
                fontSize: isSmallScreen ? '0.9rem' : '0.875rem'
              })
            }}
          />
        </div>
        <h4 style={{
          fontSize: isSmallScreen ? '1rem' : '1.25rem',
          margin: isSmallScreen ? '12px 0 8px 0' : '16px 0 12px 0'
        }}>
          Selected Timezone:
        </h4>
        <div
          style={{
            backgroundColor: "#ccc",
            padding: isSmallScreen ? "12px" : "20px",
            borderRadius: "5px",
            maxWidth: isSmallScreen ? "100%" : "600px",
            overflowX: "auto",
            width: "100%",
            boxSizing: "border-box"
          }}
        >
          <pre
            style={{
              margin: isSmallScreen ? "0" : "0 20px",
              fontWeight: 500,
              fontFamily: "monospace",
              fontSize: isSmallScreen ? "0.75rem" : "0.875rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word"
            }}
          >
           <p style={{ margin: 0 }}> {formatTimezoneDisplay(selectedTimezone)} </p>
            
          </pre>
        </div>
      </DialogContent>
      <DialogActions style={{
        justifyContent: 'center',
        margin: isSmallScreen ? '8px' : '5px',
        padding: isSmallScreen ? '8px 16px 16px 16px' : '8px 24px 20px 24px',
        gap: isSmallScreen ? '8px' : '12px',
        flexWrap: 'wrap'
      }}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          style={{
            minHeight: isSmallScreen ? '44px' : 'auto',
            fontSize: isSmallScreen ? '1rem' : '0.875rem',
            minWidth: isSmallScreen ? '120px' : 'auto'
          }}
        > Save
        </Button>
        <Button
          onClick={handleCancel}
          style={{
            minHeight: isSmallScreen ? '44px' : 'auto',
            fontSize: isSmallScreen ? '1rem' : '0.875rem',
            minWidth: isSmallScreen ? '120px' : 'auto'
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default Timezone;
